<template>
  <div class="mx-dtp-anchor">
    <v-menu
      v-model="open"
      :close-on-content-click="false"
      :nudge-width="0"
      transition="scale-transition"
      max-width="480"
      min-width="480"
      eager
      :attach="attach === undefined ? false : attach"
      content-class="mx-dtp-menu"
    >
      <!-- Activador -->
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          v-bind="attrs"
          v-on="on"
          :label="label"
          :value="displayValue"
          :dense="dense"
          :disabled="disabled"
          :clearable="clearable"
          readonly
          append-icon="mdi-calendar-clock"
          @click="open = true"
          @click:append="open = true"
          @click:clear="clear()"
        />
      </template>

      <!-- Contenido -->
      <v-card>
        <v-row no-gutters>
          <!-- Calendario -->
          <v-col :cols="showTime ? 8 : 12" class="pr-0">
            <v-date-picker
              class="mx-calendar"
              v-model="date"
              :min="minDate"
              :max="maxDate"
              :first-day-of-week="1"
              locale="es-MX"
              color="primary"
              reactive
              show-current
              elevation="0"
            />
          </v-col>

          <!-- Rollers (solo si showTime = true) -->
          <v-col
            v-if="showTime"
            cols="4"
            class="pl-2 mx-roller-col"
          >
            <div class="mx-roller-head">
              <span>HH</span><span>MM</span>
            </div>

            <div class="mx-roller-wrap">
              <!-- Ventana de selección centrada -->
              <div class="mx-roller-window"></div>

              <!-- Horas -->
              <div class="mx-roller-list" ref="rollHours">
                <div
                  v-for="h in hours"
                  :key="h"
                  class="mx-roller-item"
                  :class="{ 'is-active': h === hour }"
                  @click="hour = h"
                >
                  {{ h }}
                </div>
              </div>

              <!-- Minutos -->
              <div class="mx-roller-list" ref="rollMinutes">
                <div
                  v-for="m in minutes"
                  :key="m"
                  class="mx-roller-item"
                  :class="{ 'is-active': m === minute }"
                  @click="minute = m"
                >
                  {{ m }}
                </div>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-divider class="mt-1"></v-divider>

        <!-- Acciones -->
        <v-card-actions class="justify-space-between">
          <!-- Hoy: pone fecha + hora actual -->
          <v-btn text small color="primary" @click="setNow">
            Hoy
          </v-btn>

          <v-spacer/>

          <!-- 🔹 AHORA usa @click="cancel" -->
            <v-btn text small color="grey darken-1" @click="cancel">
                Cancelar
            </v-btn>

          <v-btn color="primary" @click="confirm">
            <v-icon left small>mdi-check</v-icon>
            Aceptar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
export default {
  name: 'MxDateTimePicker',
  props: {
    value: { type: String, default: null }, // 'YYYY-MM-DD HH:mm' o 'YYYY-MM-DDTHH:mm' o solo 'YYYY-MM-DD'
    label: { type: String, default: 'Fecha (fecha y hora)' },
    dense: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    minDate: { type: String, default: null },
    maxDate: { type: String, default: null },
    outputFormat: { type: String, default: 'YYYY-MM-DD HH:mm' },
    minuteStep: { type: Number, default: 1 },
    attach: { type: [Boolean, String, Object], default: undefined },

    // 🔹 NUEVO: permite ocultar la parte de hora/minuto
    showTime: { type: Boolean, default: true }
  },
  data () {
    return {
      open: false,
      date: null,
      hour: '00',
      minute: '00',
      // 🔹 NUEVO: snapshot al abrir
      originalDate: null,
      originalHour: '00',
      originalMinute: '00'
    }
  },
  computed: {
    hours () { return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')) },
    minutes () {
      const step = Math.max(1, this.minuteStep)
      const arr = []
      for (let i = 0; i < 60; i += step) arr.push(String(i).padStart(2, '0'))
      return arr
    },
    time () { return `${this.hour}:${this.minute}` },
    displayValue () {
      if (!this.date) return ''
      // 🔹 Si no queremos mostrar hora, solo la fecha
      if (!this.showTime) {
        return this.date
      }
      return `${this.date} ${this.time}`
    }
  },
  watch: {
    value: {
        immediate: true,
        handler (v) {
        this.syncFromValue(v)
        }
    },
    open (val) {
    if (val) {
        // 🔹 Al abrir: nos sincronizamos con la prop...
        this.syncFromValue(this.value)

        // ...y guardamos el snapshot inicial (fecha + hora)
        this.originalDate = this.date
        this.originalHour = this.hour
        this.originalMinute = this.minute

        if (this.showTime) this.$nextTick(this.scrollToSelected)
        }
    },
    hour () {
        if (this.showTime) this.$nextTick(this.scrollToSelected)
    },
    minute () {
        if (this.showTime) this.$nextTick(this.scrollToSelected)
    }
  },
  methods: {
    syncFromValue (v) {
      if (!v) {
        this.date = null
        this.hour = '00'
        this.minute = '00'
        return
      }
      const s = String(v).trim()
      const [d, t] = s.includes('T') ? s.split('T') : s.split(' ')
      this.date = d
      const [hh = '00', mm = '00'] = (t || '00:00').split(':')
      this.hour = hh.padStart(2, '0').slice(0, 2)
      this.minute = mm.padStart(2, '0').slice(0, 2)
    },
    clear () {
      this.date = null
      this.hour = '00'
      this.minute = '00'
      this.$emit('input', null)
      this.$emit('change', null)
    },
    setNow () {
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      this.date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      this.hour = pad(d.getHours())
      this.minute = pad(d.getMinutes() - (d.getMinutes() % this.minuteStep))
    },
    confirm () {
      if (!this.date) return

      let out
      if (!this.showTime) {
        // solo fecha
        out = this.date
      } else {
        out = this.outputFormat.includes('T')
          ? `${this.date}T${this.time}`
          : `${this.date} ${this.time}`
      }

      this.$emit('input', out)
      this.$emit('change', out)
      this.open = false
    },
    cancel () {
        // 🔹 Revertir a lo que había al abrir el menú
        this.date = this.originalDate
        this.hour = this.originalHour
        this.minute = this.originalMinute
        this.open = false
    },
    scrollToSelected () {
      const centerScroll = (listEl, idx) => {
        if (!listEl || idx < 0) return
        const itemH = 36
        const target = idx * itemH - (listEl.clientHeight / 2 - itemH / 2)
        listEl.scrollTop = Math.max(0, target)
      }
      centerScroll(this.$refs.rollHours, this.hours.indexOf(this.hour))
      centerScroll(this.$refs.rollMinutes, this.minutes.indexOf(this.minute))
    }
  }
}
</script>

<style>
/* Columna de rollers y encabezados */
.mx-roller-col { border-left: 1px solid rgba(0,0,0,.08); }
.mx-roller-head {
  display: grid; grid-template-columns: 1fr 1fr; column-gap: 8px;
  font-size: 11px; text-transform: uppercase; letter-spacing: .5px;
  opacity: .75; margin: 2px 2px 2px;
}

/* Contenedor de listas estilo “original” */
.mx-roller-wrap {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 8px;
  height: 315px; /* similar al alto del calendario */
  padding: 0 0px;
}

/* Ventana central (franja) */
.mx-roller-window {
  position: absolute;
  left: 0; right: 0;
  top: calc(50% - 18px);
  height: 36px;
  border-radius: 8px;
  pointer-events: none;
}

/* Fades superior/inferior para imitar el look */
.mx-roller-wrap:before,
.mx-roller-wrap:after{
  content: "";
  position: absolute; left: 0; right: 0; height: 28px; pointer-events:none;
}
.mx-roller-wrap:before{
  top: 0;
}
.mx-roller-wrap:after{
  bottom: 0;
}

/* Lista e items */
.mx-roller-list{
  overflow-y: auto;
  scrollbar-width: thin;
  scroll-snap-type: y mandatory;
  padding: 0 2px;
}
.mx-roller-item{
  scroll-snap-align: center;
  height: 36px; line-height: 36px;
  text-align: center; font-size: 16px; color: #000;   /* números en negro */
  border-radius: 6px; cursor: pointer; user-select: none;
}
.mx-roller-item.is-active{
  font-weight: 700;
  background: rgba(33,150,243,.18);
}
.mx-roller-item:hover{ background: rgba(0,0,0,.06); }

/* ===== Calendario estilo limpio (como tu imagen) ===== */
.mx-calendar {
  --mx-border: rgba(0,0,0,.10);
  --mx-gray: rgba(0,0,0,.60);
  --mx-gray-weak: rgba(0,0,0,.45);
  --mx-hover: rgba(0,0,0,.06);
  --mx-selected: rgba(33,150,243,.12); /* azul muy suave */
  height: fit-content;
}

/* Header */
.mx-calendar .v-date-picker-header {
  border-bottom: 1px solid var(--mx-border);
  padding: 6px 8px;
}
.mx-calendar .v-date-picker-header__value {
  color: #000;
  font-weight: 600;
}
.mx-calendar .v-date-picker-header .v-btn--icon {
  color: var(--mx-gray);
}
/* El date-picker se adapta al ancho de su columna */
.mx-calendar.v-date-picker { width: 100%; }
.mx-calendar .v-picker__body { width: 100% !important; }

/* Asegura que la tabla no limite el ancho */
.mx-calendar .v-date-picker-table,
.mx-calendar .v-date-picker-table table { width: 100% !important; }

/* Evita paddings que “roben” ancho */
.mx-calendar .v-date-picker-table { padding: 0 !important; }
/* Encabezado de días: lu, ma, mi... en gris */
.mx-calendar .v-date-picker-table .v-date-picker-table--weekday th {
  color: var(--mx-gray-weak);
  font-weight: 600;
  font-size: 12px;
  width: 280px;
  text-transform: none;
}

/* Celdas de días: texto negro, pills rectangulares */
.mx-calendar .v-date-picker-table {
  padding: 0px 0px 0px;
}
.mx-calendar .v-date-picker-table .v-btn {
  min-width: 36px;
  height: 36px;
  border-radius: 100px;
  color: #000 !important;
  background: transparent !important;
  box-shadow: none !important;
}
.mx-calendar .v-date-picker-table .v-btn:hover {
  background: var(--mx-hover) !important;
}
.mx-calendar .v-date-picker-table .v-btn,
.mx-calendar .v-date-picker-table .v-btn.v-btn--has-bg {
  background: transparent !important;
}
.mx-calendar .v-date-picker-table .v-btn::before {
  opacity: 0 !important;
}
.mx-calendar .v-date-picker-table .v-btn {
  height: 32px;
  min-width: 32px;
  margin: 2px 3px;
  padding: 0;
  border-radius: 8px;
  box-shadow: none !important;
  color: #000 !important;
}
.mx-calendar .v-date-picker-table .v-btn .v-btn__content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.mx-calendar .v-date-picker-table table,
.mx-calendar .v-date-picker-table tbody tr > td {
  background: transparent !important;
}
.mx-calendar .v-date-picker-table .v-btn.v-btn--active {
  background: rgba(33,150,243,.12) !important;
  box-shadow: inset 0 0 0 1px rgba(33,150,243,.35) !important;
  color: #000 !important;
}
.mx-calendar .v-date-picker-table .v-btn.today:not(.v-btn--active) {
  box-shadow: inset 0 0 0 1px rgba(0,0,0,.35) !important;
}
.mx-calendar .v-date-picker-table .v-date-picker-table--date .v-btn.v-date-picker-table__current--disabled {
  color: rgba(0,0,0,.35) !important;
}
.mx-calendar .v-ripple__container { display: none; }
</style>